/* eslint-disable */

import { assertInInjectionContext, Signal, signal } from '@angular/core';
import { resource } from '@app/app/signal-backports/resource';
import { BaseResourceOptions, ResourceLoaderParams, ResourceRef } from '@app/app/signal-backports/resource_api';
import { Observable, Subscription } from 'rxjs';

/**
 * Like `ResourceOptions` but uses an RxJS-based `loader`.
 *
 * @experimental
 */
export interface RxResourceOptions<T, R> extends BaseResourceOptions<T, R> {
  loader: (params: ResourceLoaderParams<R>) => Observable<T>;
}

/**
 * Like `resource` but uses an RxJS based `loader` which maps the request to an `Observable` of the
 * resource's value.
 *
 * @experimental
 */
export function rxResource<T, R>(opts: RxResourceOptions<T, R> & { defaultValue: NoInfer<T> }): ResourceRef<T>;

/**
 * Like `resource` but uses an RxJS based `loader` which maps the request to an `Observable` of the
 * resource's value.
 *
 * @experimental
 */
export function rxResource<T, R>(opts: RxResourceOptions<T, R>): ResourceRef<T | undefined>;
export function rxResource<T, R>(opts: RxResourceOptions<T, R>): ResourceRef<T | undefined> {
  opts?.injector || assertInInjectionContext(rxResource);
  return resource<T, R>({
    ...opts,
    loader: undefined as never,
    stream: (params) => {
      // `abort` can fire synchronously while the subscription is not initialized yet.
      // Use this flag to unsubscribe immediately once `sub` exists.
      let sub: Subscription | undefined;
      let aborted = false;

      // Start off stream as undefined.
      const stream = signal<{ value: T } | { error: unknown }>({ value: undefined as T });
      let resolve: ((value: Signal<{ value: T } | { error: unknown }>) => void) | undefined;
      const promise = new Promise<Signal<{ value: T } | { error: unknown }>>((r) => (resolve = r));
      let hasResolved = false;

      function send(value: { value: T } | { error: unknown }): void {
        stream.set(value);
        if (!hasResolved) {
          hasResolved = true;
          resolve?.(stream);
          resolve = undefined;
        }
      }

      // Track the abort listener so it can be removed if the Observable completes (as a memory
      // optimization).
      const onAbort = () => {
        aborted = true;
        sub?.unsubscribe();
        // Remove the listener immediately since unsubscribe won't trigger the subscription's
        // error/complete handlers. This ensures the promise resolves and PendingTask is released.
        params.abortSignal.removeEventListener('abort', onAbort);
        // Resolve the promise with the current stream state if it hasn't been resolved yet.
        // This ensures the PendingTask created for this request is released.
        if (!hasResolved) {
          hasResolved = true;
          resolve?.(stream);
          resolve = undefined;
        }
      };
      params.abortSignal.addEventListener('abort', onAbort);

      sub = opts.loader(params).subscribe({
        next: (value) => send({ value }),
        error: (error) => {
          send({ error });
          params.abortSignal.removeEventListener('abort', onAbort);
        },
        complete: () => {
          if (!hasResolved) {
            send({ error: new Error('Resource completed before producing a value') });
          }
          params.abortSignal.removeEventListener('abort', onAbort);
        },
      });

      if (aborted) {
        sub.unsubscribe();
      }

      if (hasResolved) {
        return Promise.resolve(stream);
      }

      return promise;
    },
  });
}
